import { createElement, CSSProperties, FunctionComponent, JSX, ReactNode } from 'react';
import { Panel as V4Panel } from 'react-resizable-panels';
import { PanelSize, toPanelSize } from './toPanelSize';

export interface IPanelProps {
  id?: string;
  minSize?: PanelSize;
  maxSize?: PanelSize;
  defaultSize?: PanelSize;
  collapsedSize?: PanelSize;
  collapsible?: boolean;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
  // v4 removed the `tagName` prop (Panel always renders a div). To keep semantic landmarks
  // (main/nav/aside) we render the requested element inside the panel. It is a full-height flex
  // column — the layout the v3 panel provided — so header rows (Toolbar/actions) plus a
  // height:100% scroll area (SimpleBar) size correctly instead of overflowing into a stray scrollbar.
  tagName?: keyof JSX.IntrinsicElements;
  children?: ReactNode;
}

const SEMANTIC_ELEMENT_STYLE: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  minHeight: 0,
};

// Compatibility wrapper over react-resizable-panels v4 `Panel`. Accepts numeric sizes as
// percentages (v3 semantics) and restores `tagName` via a nested semantic element.
export const Panel: FunctionComponent<IPanelProps> = ({
  minSize,
  maxSize,
  defaultSize,
  collapsedSize,
  tagName,
  children,
  ...rest
}) => {
  const content = tagName
    ? createElement(tagName, { style: SEMANTIC_ELEMENT_STYLE }, children)
    : children;

  return (
    <V4Panel
      minSize={toPanelSize(minSize)}
      maxSize={toPanelSize(maxSize)}
      defaultSize={toPanelSize(defaultSize)}
      collapsedSize={toPanelSize(collapsedSize)}
      {...rest}
    >
      {content}
    </V4Panel>
  );
};
